import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import { DASH } from '@/globals';
import { getDiff } from '@/utils/Diff';
import Validatable, { IComplexValidationRule } from '@/validator/Validatable';
import i18n from '@/vueI18n';

import OrganizationCustomFieldTypeOptions from './OrganizationCustomFieldTypeOptions';
import OrganizationCustomFieldValue, { IOrganizationCustomFieldValueApi } from './OrganizationCustomFieldValue';

interface IOrganizationCustomFieldApi {
  organizationcustomfield_id: number;
  organization_id?: number;
  key: string;
  name: string;
  entity: string;
  is_required: boolean;
  created_on_utc: string;
  modified_on_utc: string;
  is_dropdown: boolean;
  organizationcustomvalues: IOrganizationCustomFieldValueApi[];
}

export default class OrganizationCustomField extends Validatable {
  apiObject: IOrganizationCustomFieldApi;

  organizationID?: number | null;
  key: string;
  name: string;
  type: string;
  isRequired: boolean;
  createdOnUtc: DateTime;
  modifiedOnUtc: DateTime;
  timestamp: DateTime;
  isDropdown: boolean;
  organizationCustomValues: OrganizationCustomFieldValue[];

  constructor(
    id = -1,
    organizationID?: number | null,
    key = '',
    name = '',
    type = OrganizationCustomFieldTypeOptions.Campaign.value,
    isRequired = false,
    createdOnUtc: string | DateTime = DateTime.utc(),
    modifiedOnUtc: string | DateTime = DateTime.utc(),
    isDropdown = false,
    organizationCustomValues = [],
  ) {
    super();
    this.id = id;
    this.organizationID = organizationID;
    this.key = key;
    this.name = name;
    this.type = type;
    this.isRequired = isRequired;
    this.createdOnUtc =
      typeof createdOnUtc === 'string' ? DateTime.fromISO(createdOnUtc, { zone: 'utc' }) : createdOnUtc;
    this.modifiedOnUtc =
      typeof modifiedOnUtc === 'string' ? DateTime.fromISO(modifiedOnUtc, { zone: 'utc' }) : modifiedOnUtc;
    this.timestamp = DateTime.local();
    this.isDropdown = isDropdown;
    this.organizationCustomValues = organizationCustomValues;
  }

  get endpoint(): string {
    return `/org-custom-fields/${this.id}`;
  }

  get typeDescription(): string | undefined {
    return OrganizationCustomFieldTypeOptions.options().find((x) => x.value === this.type)?.name;
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc.toLocaleString(DateTime.DATETIME_MED);
  }

  get isRequiredDescription(): string {
    if (this.isRequired) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get isDropdownDescription(): string {
    return this.isDropdown ? capitalize(i18n.global.t('actions.yes')) : capitalize(i18n.global.t('actions.no'));
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc.toLocaleString(DateTime.DATETIME_MED);
  }

  get organizationCustomValuesFormatted(): string {
    return this.organizationCustomValues.map((x) => x.value).join(', ') ?? DASH;
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = ['created_on_utc', 'modified_on_utc', 'organizationcustomvalues'];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    if (diffs.length === 0) return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  static fromObject(obj: Record<string, unknown>): OrganizationCustomField {
    return super.fromObject(obj, new OrganizationCustomField());
  }

  static fromApi(apiObject: IOrganizationCustomFieldApi): OrganizationCustomField {
    const organizationCustomField = new OrganizationCustomField(
      apiObject.organizationcustomfield_id as number,
      apiObject.organization_id as number | null,
      apiObject.key as string,
      apiObject.name as string,
      apiObject.entity as string,
      apiObject.is_required as boolean,
      DateTime.fromISO(apiObject.created_on_utc as string, { zone: 'utc' }),
      DateTime.fromISO(apiObject.modified_on_utc as string, { zone: 'utc' }),
      apiObject.is_dropdown as boolean,
      (apiObject.organizationcustomvalues as IOrganizationCustomFieldValueApi[])?.map((x) =>
        OrganizationCustomFieldValue.fromApi(x),
      ) ?? [],
    );
    organizationCustomField.apiObject = apiObject;
    return organizationCustomField;
  }

  toApi(): Record<string, unknown> {
    const result = {
      organizationcustomfield_id: this.id >= 0 ? this.id : undefined,
      organization_id: this.organizationID,
      key: this.key,
      name: this.name,
      entity: this.type,
      is_required: this.isRequired,
      created_on_utc: undefined,
      modified_on_utc: undefined,
      is_dropdown: this.isDropdown,
    };

    if (this.createdOnUtc) {
      result.created_on_utc = this.createdOnUtc.toISO();
    }

    if (this.modifiedOnUtc) {
      result.modified_on_utc = this.modifiedOnUtc.toISO();
    }

    return result;
  }

  static validatorCreateRules(): Record<string, string | IComplexValidationRule> {
    return {
      key: 'required|max:50|not_empty_or_whitespace',
      name: 'required|max:50|not_empty_or_whitespace',
      type: 'required',
    };
  }
}
